import { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import type { Json } from "@/integrations/supabase/types";
import { permissionsSatisfy, type Permission } from "@/lib/permissions";

export interface Workspace {
  id: string;
  name: string;
  slug: string | null;
  plan: string;
  status: string;
  settings: Json;
  created_by_user_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceMember {
  id: string;
  workspace_id: string;
  user_id: string;
  role: "owner" | "admin" | "member" | "accountant" | "read_only";
  status: string;
  custom_permissions: string[] | null;
  created_at: string;
}

interface WorkspaceContextType {
  currentWorkspace: Workspace | null;
  workspaces: Workspace[];
  currentMembership: WorkspaceMember | null;
  isLoading: boolean;
  error: Error | null;
  switchWorkspace: (workspaceId: string) => void;
  refreshWorkspaces: () => Promise<void>;
  updateWorkspace: (workspaceId: string, updates: { name?: string }) => Promise<void>;
  isOwner: boolean;
  isAdmin: boolean;
  canEdit: boolean;
  /**
   * Check whether the current member can perform a permission. Honors the
   * `.any` → `.own` implication (matches the SQL `has_member_permission`).
   * Owner role bypasses; admin role bypasses unless `custom_permissions` is
   * explicitly populated (scoped-admin case).
   */
  canDo: (permission: Permission) => boolean;
}

const WorkspaceContext = createContext<WorkspaceContextType>({
  currentWorkspace: null,
  workspaces: [],
  currentMembership: null,
  isLoading: true,
  error: null,
  switchWorkspace: () => {},
  refreshWorkspaces: async () => {},
  updateWorkspace: async () => {},
  isOwner: false,
  isAdmin: false,
  canEdit: false,
  canDo: () => false,
});

const WORKSPACE_STORAGE_KEY = "syntra-current-workspace";

/**
 * A brand-new account's workspace + owner membership are created by the
 * `on_profile_created` trigger, which runs while the client already holds a
 * session. Reading `workspace_members` a beat too early used to settle on
 * "no workspace" for good — empty sidebar, skeleton dashboard, fixed only by a
 * reload. Retry a few times before believing an empty result.
 */
const MEMBERSHIP_RETRY_DELAYS_MS = [400, 800, 1600];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [memberships, setMemberships] = useState<WorkspaceMember[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const fetchIdRef = useRef(0);

  // Mirrors `workspaces` so `switchWorkspace` can resolve an id that was
  // fetched moments ago. Callers that create a workspace then immediately
  // switch (`await refreshWorkspaces(); switchWorkspace(id)`) still hold the
  // pre-refresh `workspaces` array in their closure — the state update hasn't
  // re-rendered them yet — so the id looked unknown and the switch silently
  // did nothing. A ref is current the moment the fetch writes it.
  const workspacesRef = useRef<Workspace[]>([]);
  const commitWorkspaces = useCallback((list: Workspace[]) => {
    workspacesRef.current = list;
    setWorkspaces(list);
  }, []);

  // Get current membership
  const currentMembership = memberships.find(
    (m) => m.workspace_id === currentWorkspace?.id
  ) || null;

  // Role checks
  const isOwner = currentMembership?.role === "owner";
  const isAdmin = currentMembership?.role === "owner" || currentMembership?.role === "admin";
  const canEdit = currentMembership?.role !== "read_only";
  const canDo = (permission: Permission): boolean => {
    if (!currentMembership) return false;
    // Owner: absolute. Admin: full unless custom_permissions is populated
    // (lets us configure a "scoped admin" restricted to certain sections).
    if (currentMembership.role === "owner") return true;
    if (currentMembership.role === "admin" &&
        (currentMembership.custom_permissions === null ||
         currentMembership.custom_permissions.length === 0)) {
      return true;
    }
    return permissionsSatisfy(currentMembership.custom_permissions, permission);
  };

  // Fetch workspaces for the current user.
  //
  // Dep is `user?.id` (a primitive) — NOT the whole `user` object. Supabase
  // emits TOKEN_REFRESHED on tab focus and our `onAuthStateChange` handler
  // calls `setUser(session?.user)` with a fresh object reference. Depending
  // on the whole object caused this effect to refire on every tab switch,
  // re-fetching workspace_members + workspaces and replacing the
  // `currentWorkspace` reference, which then cascaded into downstream hooks
  // (e.g. `usePipeline.fetchStageHistory`).
  const fetchWorkspaces = useCallback(async () => {
    // Only the newest fetch may write state — the retry loop below awaits
    // across several hundred ms, long enough for a sign-out or user switch to
    // land in the middle of it.
    const fetchId = ++fetchIdRef.current;
    const isStale = () => fetchIdRef.current !== fetchId;

    if (!user?.id) {
      commitWorkspaces([]);
      setMemberships([]);
      setCurrentWorkspace(null);
      setIsLoading(false);
      return;
    }

    const userId = user.id;

    try {
      setIsLoading(true);
      setError(null);

      // Fetch user's workspace memberships
      const fetchMemberships = async () => {
        const { data, error: membershipError } = await supabase
          .from("workspace_members")
          .select("*")
          .eq("user_id", userId)
          .eq("status", "active");

        if (membershipError) throw membershipError;
        return data ?? [];
      };

      let membershipData = await fetchMemberships();
      for (const delay of MEMBERSHIP_RETRY_DELAYS_MS) {
        if (membershipData.length > 0) break;
        await sleep(delay);
        if (isStale()) return;
        membershipData = await fetchMemberships();
      }

      if (isStale()) return;

      setMemberships(membershipData);

      if (membershipData.length === 0) {
        commitWorkspaces([]);
        setCurrentWorkspace(null);
        setIsLoading(false);
        return;
      }

      // Fetch the actual workspaces
      const workspaceIds = membershipData.map((m) => m.workspace_id);
      const { data: workspaceData, error: workspaceError } = await supabase
        .from("workspaces")
        .select("*")
        .in("id", workspaceIds)
        .eq("status", "active");

      if (workspaceError) throw workspaceError;
      if (isStale()) return;

      commitWorkspaces(workspaceData || []);

      // Restore last selected workspace or pick first one
      const storedWorkspaceId = localStorage.getItem(WORKSPACE_STORAGE_KEY);
      const targetWorkspace = workspaceData?.find((w) => w.id === storedWorkspaceId) 
        || workspaceData?.[0] 
        || null;

      setCurrentWorkspace(targetWorkspace);

      if (targetWorkspace) {
        localStorage.setItem(WORKSPACE_STORAGE_KEY, targetWorkspace.id);
      }
    } catch (err) {
      if (isStale()) return;
      console.error("Error fetching workspaces:", err);
      setError(err instanceof Error ? err : new Error("Failed to fetch workspaces"));
      toast.error("Failed to load workspaces");
    } finally {
      if (!isStale()) setIsLoading(false);
    }
  }, [user?.id]);

  // Switch to a different workspace
  const switchWorkspace = useCallback((workspaceId: string) => {
    const workspace = workspacesRef.current.find((w) => w.id === workspaceId);
    if (!workspace) {
      // Genuinely unknown id (revoked membership, stale deep link). Silently
      // ignoring it stranded the user on the previous workspace with no clue.
      console.error("switchWorkspace: unknown workspace id", workspaceId);
      toast.error("That workspace is no longer available");
      return;
    }
    setCurrentWorkspace(workspace);
    localStorage.setItem(WORKSPACE_STORAGE_KEY, workspaceId);
    toast.success(`Switched to ${workspace.name}`);
  }, []);

  // Persist workspace changes (e.g. rename) to the `workspaces` table and
  // reflect them in local state without a full refetch. Only the workspace
  // owner or its original creator may rename it.
  const updateWorkspace = useCallback(
    async (workspaceId: string, updates: { name?: string }) => {
      const target = workspaces.find((w) => w.id === workspaceId);
      const membership = memberships.find((m) => m.workspace_id === workspaceId);
      const canManage =
        membership?.role === "owner" || target?.created_by_user_id === user?.id;

      if (!canManage) {
        toast.error("Only the workspace owner can rename it");
        throw new Error("Insufficient permissions to update workspace");
      }

      const { data, error } = await supabase
        .from("workspaces")
        .update(updates)
        .eq("id", workspaceId)
        .select()
        .single();

      if (error) {
        toast.error(error.message || "Failed to update workspace");
        throw error;
      }

      const updated = data as Workspace;
      setWorkspaces((prev) => prev.map((w) => (w.id === workspaceId ? updated : w)));
      setCurrentWorkspace((prev) => (prev?.id === workspaceId ? updated : prev));
      toast.success("Workspace updated");
    },
    [workspaces, memberships, user?.id],
  );

  // Fetch workspaces when user changes
  useEffect(() => {
    fetchWorkspaces();
  }, [fetchWorkspaces]);

  return (
    <WorkspaceContext.Provider
      value={{
        currentWorkspace,
        workspaces,
        currentMembership,
        isLoading,
        error,
        switchWorkspace,
        refreshWorkspaces: fetchWorkspaces,
        updateWorkspace,
        isOwner,
        isAdmin,
        canEdit,
        canDo,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return context;
};
